import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import classnames from 'classnames';

import preventDefaultPropagation from 'client/helpers/preventDefaultPropagation';

import { formatName, formatAgoDate } from 'common/formatters';

import { getHouseholdQualification } from 'common/householdApplication/prequalificationHelpers';

import { DATA_TABLE_SOURCE_LISTING_LEADS } from 'common/dataTableConstants';

import { getLeadStageSettings } from 'common/leadConstants';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrFrontConversationView from 'client/components/SrFrontConversationView';
import SrHouseholdPrequalSummary from 'client/components/SrHouseholdPrequalSummary';
import SrPartnerListingTourReviews from 'client/components/SrPartnerListingTourReviews';
import { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrDataTableUI from 'client/components/SrDataTableUI';

import './SrPartnerLeadsCard.less';

function getScheduledToursRow({ tourStartDate, rentlinxAvailableDates }) {
  const nowDateString = moment().format('YYYY-MM-DD');

  const scheduledToursDates = tourStartDate
    ? [tourStartDate]
    : rentlinxAvailableDates
        .map(({ date }) => date)
        .filter((startDate) => moment(startDate).isSameOrAfter(moment(nowDateString)));

  const scheduledToursText = scheduledToursDates
    .map((startDate) => {
      return moment(startDate).calendar(null, {
        sameDay: '[Today]',
        nextDay: '[Tomorrow]',
        nextWeek: 'MMM D',
        lastDay: 'MMM D',
        lastWeek: 'MMM D',
        sameElse: 'MMM D',
      });
    })
    .join(', ');

  if (!scheduledToursText) {
    return null;
  }

  return {
    text: scheduledToursText,
    style: tourStartDate ? 'tag' : 'text',
  };
}

const getLeadTourReviews = ({ userId, listingTourReviews }) => {
  return (listingTourReviews || []).filter(({ tours }) => (tours[0] || {}).userId === userId);
};

const SrBulletPoints = ({ points }) => {
  const dots = [
    points >= 10 ? 'green' : points >= 5 ? 'lightGreen' : 'lightRed',
    points >= 20 ? 'green' : 'grey',
    points >= 30 ? 'green' : 'grey',
  ];

  return (
    <div>
      {dots.map((dotStyle, index) => (
        <div
          key={index} // eslint-disable-line react/no-array-index-key
          styleName={classnames('dot', dotStyle)}
        />
      ))}
    </div>
  );
};

SrBulletPoints.displayName = 'SrBulletPoints';

SrBulletPoints.propTypes = {
  points: PropTypes.number.isRequired,
};

const EmptyCell = () => (
  <SrText font="greycliff" size="18px" weight="800" color="grey600">
    -
  </SrText>
);

const SrPartnerLeadsComponent = ({
  leads,
  viewSettings,
  dataTableApiState,
  onInfiniteScrollWrapperRef,
  listingTourReviews,
  currentUser,
  partner,
  households,
  dispatch,
  location,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const householdsById = households.reduce((memo, household) => {
    memo[household.id] = household;
    return memo;
  }, {});

  return (
    <div className="SrPartnerLeadsCard" ref={onInfiniteScrollWrapperRef}>
      <SrPartnerSection
        title={`Leads List${
          typeof leads?.length === 'number' && (leads.length > 0 || !dataTableApiState.inProgress)
            ? ` (${leads.length})`
            : ''
        }`}
      >
        <SrCard noContentPadding>
          <SrDataTableUI
            sticky
            theme={SR_TABLE_THEME_PARTNER_CLEAR}
            dataTableViewSettings={viewSettings}
            headers={[
              {
                content: 'Name',
                dataTableFieldId: 'name',
                sortPosition: 'right',
              },
              {
                content: 'Interest',
                dataTableFieldId: 'interest',
                sortPosition: 'right',
              },
              {
                content: 'Last Contact',
                dataTableFieldId: 'lastContactedAt',
                sortPosition: 'right',
              },
              {
                content: 'Feedback',
                dataTableFieldId: 'tourRating',
                sortPosition: 'right',
              },
              {
                content: 'Tour',
              },
              {
                content: '',
                style: { width: '16px' },
              },
            ]}
            rows={leads.reduce(
              (
                memo,
                {
                  id,
                  userId,
                  householdId,
                  name,
                  stage,
                  interest,
                  lastContactedAt,
                  tourRating,
                  rentlinxAvailableDates,
                  tourStartDate,
                  householdAppsCompleted,
                  householdAppsTotal,
                  appDeliveredAt,
                  appApprovedAt,
                  isArchived,
                  frontConversationId,
                },
              ) => {
                const tags = lodashCompact([
                  householdAppsCompleted > 0 &&
                    householdAppsTotal === householdAppsCompleted &&
                    !isArchived &&
                    !appDeliveredAt &&
                    !appApprovedAt && {
                      styleName: 'tag-theme-purple-inverted',
                      label: 'Apps Processing',
                    },
                  !isArchived &&
                    appDeliveredAt && {
                      styleName: 'tag-theme-purple',
                      label: 'Apps Needs Review',
                    },
                  !isArchived &&
                    appApprovedAt && {
                      styleName: 'tag-theme-green',
                      label: 'Lease Pending',
                    },
                ]);

                const scheduledTourRow = getScheduledToursRow({
                  tourStartDate,
                  rentlinxAvailableDates,
                });
                const isExpanded = expandedRows[id];

                memo.push({
                  onClick: (e) => {
                    preventDefaultPropagation(e);

                    setExpandedRows({
                      ...expandedRows,
                      [id]: !expandedRows[id],
                    });
                  },
                  cells: [
                    {
                      content: formatName(name, 'shortAbbr'),
                      style: {
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      },
                    },
                    {
                      content: (
                        <span styleName="tooltip">
                          <SrBulletPoints points={interest} />
                          <span styleName="text">{getLeadStageSettings(stage)?.label}</span>
                        </span>
                      ),
                    },
                    {
                      content: (
                        <span styleName="tooltip">
                          <SrText font="greycliff" size="16" weight="600">
                            {lastContactedAt ? (
                              formatAgoDate(lastContactedAt, { useDays: true })
                            ) : (
                              <EmptyCell />
                            )}
                          </SrText>
                          <span styleName="text">
                            How many days ago the renter last contacted Sunroom.
                          </span>
                        </span>
                      ),
                    },
                    {
                      content:
                        typeof tourRating !== 'number' ? (
                          <EmptyCell />
                        ) : (
                          <div styleName={classnames('starRating', { red: tourRating < 5 })}>
                            {tourRating}
                            <div styleName={classnames('star', { red: tourRating < 5 })} />
                          </div>
                        ),
                    },
                    {
                      content: (
                        <>
                          <div styleName="tags">
                            {scheduledTourRow && (
                              <SrBox maxWidth="150px">
                                {scheduledTourRow.style === 'text' ? (
                                  <SrText
                                    font="greycliff"
                                    color="green"
                                    size="16"
                                    weight="800"
                                    lineHeight="30px"
                                    whiteSpace="nowrap"
                                    textOverflow="ellipsis"
                                  >
                                    {scheduledTourRow.text}
                                  </SrText>
                                ) : (
                                  <SrBox
                                    backgroundColor="#FFF5CC"
                                    borderRadius="7px"
                                    height="30px"
                                    lineHeight="30px"
                                    padding="0 10px"
                                    whiteSpace="nowrap"
                                    textOverflow="ellipsis"
                                    overflow="hidden"
                                  >
                                    <SrText
                                      font="greycliff"
                                      size="16"
                                      weight="700"
                                      style={{ color: '#665200' }}
                                    >
                                      {scheduledTourRow.text}
                                    </SrText>
                                  </SrBox>
                                )}
                              </SrBox>
                            )}
                            {tags.length === 0
                              ? null
                              : tags.map(({ label, styleName }) => (
                                  <span styleName={classnames('tag', styleName)} key={label}>
                                    {label}
                                  </span>
                                ))}
                            {tags.length === 0 && !scheduledTourRow && <EmptyCell />}
                          </div>
                        </>
                      ),
                    },
                    {
                      content: (
                        <div styleName="arrowBox">
                          <div styleName={classnames('arrow', { up: isExpanded })} />
                        </div>
                      ),
                    },
                  ],
                });

                if (isExpanded) {
                  const household = householdsById[householdId];
                  const householdQualification = household
                    ? getHouseholdQualification({
                        moveInDate: household.moveInDate,
                        listing: household.listing,
                        tenantScreeningCriteria: {
                          ...household.tenantScreeningCriteria,
                          breedRestrictions: household.breedRestrictions,
                        },
                        renters: household.renters,
                        householdApplications: household.householdApplications,
                        householdGuarantors: household.householdGuarantors,
                        guarantorApplications: household.guarantorApplications,
                        tenantAlertReports: household.tenantAlertReports,
                        leadRenterId: household.leadRenterId,
                        leaseStartDate: household.leaseStartDate,
                        partner,
                      })
                    : null;

                  memo.push({
                    cells: [
                      {
                        noPadding: true,
                        colSpan: 6,
                        content: (
                          <div>
                            <SrBox
                              padding="30px 25px 40px"
                              minHeight="100%"
                              backgroundColor="#EBF4FF"
                            >
                              <SrBox marginBottom="15px">
                                <SrText display="block" font="greycliff" weight="800" size="18">
                                  "Interest" dot details
                                </SrText>
                              </SrBox>
                              <SrText display="block" font="greycliff" weight="600" size="18">
                                {getLeadStageSettings(stage)?.label}
                              </SrText>
                              {tourStartDate && (
                                <SrCard>
                                  <SrText font="greycliff" weight="700" size="18">
                                    Tour Feedback
                                  </SrText>

                                  <SrPartnerListingTourReviews
                                    tourReviews={getLeadTourReviews({
                                      listingTourReviews,
                                      userId,
                                    })}
                                    currentUser={currentUser}
                                    partner={partner}
                                    dispatch={dispatch}
                                    location={location}
                                    showLeadInfo={false}
                                    noContainer
                                  />
                                </SrCard>
                              )}
                              {householdQualification && (
                                <SrCard>
                                  <SrHouseholdPrequalSummary
                                    partner={partner}
                                    currentUser={currentUser}
                                    listing={household.listing}
                                    concession={household.concession}
                                    householdListing={household.householdListing}
                                    tenantScreeningCriteria={{
                                      ...household.tenantScreeningCriteria,
                                      breedRestrictions: household.breedRestrictions,
                                    }}
                                    rentersQualifications={
                                      householdQualification.rentersQualifications
                                    }
                                    householdRequirements={
                                      householdQualification.householdRequirements
                                    }
                                    rentersVerifications={
                                      householdQualification.rentersVerifications
                                    }
                                    guarantorsVerifications={
                                      householdQualification.guarantorsVerifications
                                    }
                                    householdVerifications={
                                      householdQualification.householdVerifications
                                    }
                                    householdApplications={household.householdApplications}
                                    guarantorApplications={household.guarantorApplications}
                                    tenantAlertReports={household.tenantAlertReports}
                                    householdAccepted={household.householdAccepted}
                                  />
                                </SrCard>
                              )}

                              {frontConversationId && (
                                <SrCard>
                                  <SrBox maxHeight="340px" overflow="scroll">
                                    <SrFrontConversationView
                                      user={{
                                        id: userId,
                                        name: name,
                                      }}
                                      frontConversationId={frontConversationId}
                                      dispatch={dispatch}
                                    />
                                  </SrBox>
                                </SrCard>
                              )}
                            </SrBox>
                          </div>
                        ),
                      },
                    ],
                  });
                }

                return memo;
              },
              [],
            )}
          />
          {dataTableApiState.inProgress && (
            <SrFlex justify="center">
              <div className="loading-gif clearfix" />
            </SrFlex>
          )}
        </SrCard>
      </SrPartnerSection>
    </div>
  );
};

const SrPartnerLeadsCard = ({
  listingId,
  dispatch,
  currentUser,
  partner,
  location,
  listingTourReviews,
  households,
}) => {
  return (
    <SrDataTableConnected
      itemsPropName="leads"
      dataSource={DATA_TABLE_SOURCE_LISTING_LEADS}
      count={500}
      initialViewSettings={{
        sort: {
          interest: 'desc',
        },
        filter: {
          listingId,
        },
      }}
    >
      {({ leads, viewSettings, dataTableApiState, onInfiniteScrollWrapperRef }) => {
        return (
          <SrPartnerLeadsComponent
            leads={leads}
            viewSettings={viewSettings}
            dataTableApiState={dataTableApiState}
            onInfiniteScrollWrapperRef={onInfiniteScrollWrapperRef}
            households={households}
            listingTourReviews={listingTourReviews}
            currentUser={currentUser}
            partner={partner}
            dispatch={dispatch}
            location={location}
          />
        );
      }}
    </SrDataTableConnected>
  );
};

SrPartnerLeadsCard.propTypes = {
  listingId: PropTypes.number.isRequired,
  dispatch: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  partner: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  listingTourReviews: PropTypes.array,
};

SrPartnerLeadsCard.displayName = 'SrPartnerLeadsCard';

export default SrPartnerLeadsCard;
